import { Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, useGridState, ButtonContainer } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { TransientWorkbookGridDto } from "../../../../../api";
import { ERoutes } from "../../../../../ERoutes";
import { AddButton, CustomDataTable, DeleteButton, WorkbookTypeIcon } from "../../../../../components";
import { useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { WorkbookDialog } from "./WorkbookDialog";
import styled from "styled-components";

export interface ITransientWorkbookListProps {
  dogId: number;
  onWorkbookChanged: (data: TransientWorkbookGridDto[]) => void;
  initialWorkbooks: TransientWorkbookGridDto[];
  editMode: boolean;
  loading: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TransientWorkbookList: React.FunctionComponent<ITransientWorkbookListProps> = ({
  dogId,
  onWorkbookChanged,
  initialWorkbooks,
  editMode,
  loading,
}) => {
  const { t } = useTl();

  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [workbooks, setWorkbooks] = React.useState(initialWorkbooks);

  const tableState = useGridState<any>({
    serverMode: false,
    enablePagination: false,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { identifier: "DESC" },
  });

  const { setData } = tableState;

  React.useEffect(() => {
    setData(workbooks);
  }, [setData, workbooks]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: TransientWorkbookGridDto) => (
          <ButtonContainer>
            <DeleteButton
              minimal={true}
              onDelete={() => {
                const newWorkbooks = workbooks.filter((e) => e !== row);
                setWorkbooks(newWorkbooks);
                onWorkbookChanged(newWorkbooks);
              }}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Identifier),
        fieldName: "identifier",
      },
      {
        header: () => t(ETLCodes.Counter),
        fieldName: "counter",
      },
      {
        header: () => t(ETLCodes.WorkbookType),
        fieldName: "workbookType",
        render: (row: TransientWorkbookGridDto) => (
          <Link target="_blank" to={`${ERoutes.workbookType}/${row.workbookTypeId}/detail/view`}>
            {row.workbookType}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.WithPedigree),
        fieldName: "withPedigree",
        alignment: "center",
        autoFitContent: true,
        render: (row: TransientWorkbookGridDto) => <WorkbookTypeIcon workbookTypeId={row.workbookTypeId} />,
      },
    ],
    [editMode, onWorkbookChanged, t, workbooks]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);

  return (
    <>
      <FieldSet
        title={
          <Container>
            <div>{t(ETLCodes.Workbooks)}</div>
            {editMode && (
              <div>
                <AddButton
                  onClick={(e) => {
                    e.stopPropagation();
                    addItemFunction();
                  }}
                  text={t(ETLCodes.General_Add)}
                  intent={Intent.PRIMARY}
                />
              </div>
            )}
          </Container>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>

      <Dialog
        title={t(ETLCodes.Workbook)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 250, zIndex: 20, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <WorkbookDialog
            dogId={dogId}
            initialWorkbooks={workbooks}
            onSaved={(newItem: TransientWorkbookGridDto) => {
              setDialogOpened(false);
              const newWorkbooks = [...workbooks, newItem];
              setWorkbooks(newWorkbooks);
              onWorkbookChanged(newWorkbooks);
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
